class AnnouncementBar extends HTMLElement {
  constructor() {
    super();

    this.config = {
      autorotate: this.dataset.autorotate === 'true',
      autorotateSpeed: parseInt(this.dataset.autorotateSpeed) * 1000,
      moveTime: parseFloat(this.dataset.speed), // 100px going to move for
      space: 100, // 100px
    };

    if (theme.config.mqlSmall) {
      this.initOnMobile();
    }
    else {
      this.initOnDesktop();
    }

    document.addEventListener('matchSmall', () => {
      this.unload();
      this.initOnMobile();
    });

    document.addEventListener('unmatchSmall', () => {
      this.unload();
      this.initOnDesktop();
    });
  }

  initOnDesktop() {
    switch(this.dataset.layout) {
      case 'carousel':
        this.initCarousel();
        break;

      case 'marquee':
        this.initMarquee();
        break;

      default:
        this.initMarqueeOptimized();
    }
  }

  initOnMobile() {
    switch(this.dataset.mobileLayout) {
      case 'carousel':
        this.initCarousel();
        break;

      case 'marquee':
        this.initMarquee();
        break;
    }
  }

  unload() {
    this.stopAutoplay();
    this.removeNextPrevListeners();
    this.classList.remove('is-carousel');

    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    const slider = this.querySelector('.announcement-slider');
    if (slider) slider.classList.remove('swiper');

    if (this.flickity && typeof this.flickity.destroy === 'function') {
      this.flickity.destroy();
      this.flickity = null;
    }

    if (this.classList.contains('marquee')) {
      this.classList.remove('marquee');
      this.querySelectorAll('.announcement-slider[aria-hidden]').forEach((clone) => {
        clone.remove();
      });
    }

    if (this.observer && typeof this.observer.unobserve === 'function') {
      this.observer.unobserve(this);
    }
  }

  initCarousel() {
    this.classList.add('is-carousel');
    const container = this.querySelector('.announcement-slider');
    this.slides = Array.from(this.querySelectorAll('.announcement-slider__slide'));
    this.currentIndex = 0;

    this.applySlideColors(this.slides[0]);

    if (!container || this.slides.length <= 1 || typeof Swiper === 'undefined') {
      return;
    }

    container.classList.add('swiper');

    this.swiper = new Swiper(container, {
      direction: 'vertical',
      loop: true,
      speed: 450,
      slidesPerView: 1,
      allowTouchMove: true,
      watchOverflow: true,
      autoHeight: true,
      autoplay: this.config.autorotate
        ? {
            delay: this.config.autorotateSpeed,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }
        : false,
      navigation: {
        nextEl: this.querySelector('button[name="next"]'),
        prevEl: this.querySelector('button[name="previous"]'),
      },
      on: {
        slideChange: (swiper) => {
          const slide = swiper.slides[swiper.activeIndex];
          this.currentIndex = swiper.realIndex;
          this.applySlideColors(slide);
        },
      },
    });
  }

  applySlideColors(slide) {
    if (!slide) return;

    const background = slide.dataset.background;
    const foreground = slide.dataset.foreground;
    const bar = this.closest('.announcement-bar') || this;

    if (background) {
      this.style.setProperty('--announcement-slide-background', background);
      bar.style.setProperty('--announcement-slide-background', background);
    }

    if (foreground) {
      this.style.setProperty('--announcement-slide-foreground', foreground);
      bar.style.setProperty('--announcement-slide-foreground', foreground);
    }
  }

  selectSlide(index) {
    if (this.swiper) {
      this.swiper.slideToLoop(index);
      return;
    }

    if (!this.slides || !this.slides.length) return;
    const nextIndex = ((index % this.slides.length) + this.slides.length) % this.slides.length;
    this.currentIndex = nextIndex;
    this.slides.forEach((slide, i) => {
      slide.classList.toggle('is-selected', i === nextIndex);
    });
    this.applySlideColors(this.slides[nextIndex]);
  }

  startAutoplay() {
    if (this.swiper && this.swiper.autoplay) {
      this.swiper.autoplay.start();
    }
  }

  stopAutoplay() {
    if (this.swiper && this.swiper.autoplay) {
      this.swiper.autoplay.stop();
    }
  }

  removeNextPrevListeners() {}

  initMarqueeOptimized() {
    setTimeout(() => {
      this.marqueeAnimation();
    });

    // pause when out of view
    this.observer = new IntersectionObserver((entries, _observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.isAnimated && this.classList.remove('marquee--paused');
        }
        else {
          this.isAnimated && this.classList.add('marquee--paused');
        }
      });
    }, {rootMargin: '0px 0px 50px 0px'});

    this.observer.observe(this);

    window.addEventListener('resize', this.marqueeAnimation.bind(this));
  }

  marqueeAnimation() {
    const slider = this.querySelector('.announcement-slider');

    if (slider.scrollWidth > this.clientWidth) {
      if (!this.isAnimated) {
        for (let index = 0; index < 2; index++) {
          const clone = slider.cloneNode(true);
          clone.setAttribute('aria-hidden', true);
          this.appendChild(clone);
        }
        
        this.classList.add('marquee');
        this.style.setProperty('--duration', `${(slider.scrollWidth / this.config.space) * this.config.moveTime}s`);
        this.isAnimated = true;
      }
    }
    else if (this.isAnimated) {
      for (let index = 0; index < 2; index++) {
        this.removeChild(this.lastElementChild);
      }
      this.classList.remove('marquee');
      this.isAnimated = false;
    }
  }

  initMarquee() {
    setTimeout(() => {
      const slider = this.querySelector('.announcement-slider');

      for (let index = 0; index < 10; index++) {
        const clone = slider.cloneNode(true);
        clone.setAttribute('aria-hidden', true);
        this.appendChild(clone);
      }
  
      this.classList.add('marquee');
      this.style.setProperty('--duration', `${(slider.clientWidth / this.config.space) * this.config.moveTime}s`);
    });

    // pause when out of view
    this.observer = new IntersectionObserver((entries, _observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.classList.remove('marquee--paused');
        }
        else {
          this.classList.add('marquee--paused');
        }
      });
    }, {rootMargin: '0px 0px 50px 0px'});

    this.observer.observe(this);
  }
}
customElements.define('announcement-bar', AnnouncementBar);

document.addEventListener('shopify:block:select', (event) => {
  const slide = event.target.classList.contains('announcement-slider__slide')
    ? event.target
    : event.target.closest('.announcement-slider__slide');
  if (!slide) return;

  const announcementBar = slide.closest('announcement-bar');
  if (!announcementBar || typeof announcementBar.selectSlide !== 'function') return;

  const index = parseInt(slide.dataset.index, 10);
  if (Number.isNaN(index)) return;

  announcementBar.selectSlide(index);
  announcementBar.stopAutoplay();
});

document.addEventListener('shopify:block:deselect', (event) => {
  const slide = event.target.classList.contains('announcement-slider__slide')
    ? event.target
    : event.target.closest('.announcement-slider__slide');
  if (!slide) return;

  const announcementBar = slide.closest('announcement-bar');
  if (announcementBar && typeof announcementBar.startAutoplay === 'function') {
    announcementBar.startAutoplay();
  }
});
